import useToggle from "@/hooks/use-toggle";
import useTranslation from "@/hooks/use-translation";
import { ParameterSchemaModel, TrackerSchemaModel } from "@/models/tracker";
import { Input, Switch, Autocomplete } from "@/components/ui";
import { FaChevronDown } from "react-icons/fa";
import React from "react";
import { Controller, FieldValues, UseFormSetValue } from "react-hook-form";
import { Control } from "react-hook-form";

const CollapsibleSection: React.FC<{
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ label, open, onToggle, children }) => (
  <div className="mt-6 space-y-0">
    <button
      type="button"
      className={`w-full flex cursor-pointer items-center justify-between rounded-arc-md border px-4 py-3 transition-all duration-arc-fast text-sm font-semibold
        ${
          open
            ? "bg-accent-subtle border-accent text-accent-dark"
            : "bg-surface-elevated border-arc-border text-content-primary hover:border-arc-border-strong hover:bg-surface-secondary"
        }`}
      onClick={onToggle}
    >
      <span>{label}</span>
      <FaChevronDown
        className={`transition-transform duration-arc-normal ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && (
      <div className="mt-2 space-y-3 rounded-arc-md border border-arc-border bg-surface-secondary p-4">
        {children}
      </div>
    )}
  </div>
);

const TrackerForm: React.FC<{
  tracker: TrackerSchemaModel;
  control: Control<FieldValues, any>;
  setValue: UseFormSetValue<FieldValues>;
}> = ({ tracker, control, setValue }) => {
  const { t } = useTranslation();
  const [showEndpointOptional, toggleEndpointOptional] = useToggle();
  const [showGateway, toggleGateway] = useToggle();

  const parameters = tracker.parameters;

  // --- Endpoint parameters (from request body, specific to this scraper) ---
  const endpointRequired = parameters.filter(
    (p) => p.origin === "endpoint" && p.required && p.schema.light !== false,
  );
  const endpointOptional = parameters.filter(
    (p) => p.origin === "endpoint" && !p.required && p.schema.light !== false,
  );

  // --- Gateway parameters (query params added by gateway: format, limits, etc.) ---
  const gatewayParams = parameters.filter((p) => p.origin === "gateway");

  const displayInput = (parameter: ParameterSchemaModel, index: number) => {
    const name = "parameter." + parameter.name;

    if (
      parameter.schema.type === "integer" ||
      parameter.schema.type === "string"
    )
      return (
        <div key={index} className="w-full">
          {!parameter.schema.suggest_endpoint ? (
            <Controller
              name={name}
              control={control}
              defaultValue=""
              rules={{
                required: parameter.required ? "Ce champ est requis" : false,
                maxLength: {
                  value: 99,
                  message: "Maximum 99 caractères",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="mb-1">
                  <Input
                    {...field}
                    label={
                      parameter.name +
                      (parameter.schema.title
                        ? ` - ${parameter.schema.title}`
                        : "")
                    }
                    required={parameter.required}
                    type={
                      parameter.schema.type === "integer" ? "number" : "text"
                    }
                    placeholder={parameter.example || ""}
                    error={error?.message}
                    fullWidth
                  />
                </div>
              )}
            />
          ) : (
            <></>
          )}
        </div>
      );

    if (parameter.schema.type === "boolean")
      return (
        <div key={index} className="w-full">
          <Controller
            name={name}
            control={control}
            defaultValue={parameter.schema.default || false}
            rules={{
              required: parameter.required ? "Ce champ est requis" : false,
            }}
            render={({ field, fieldState: { error } }) => (
              <div className="flex flex-col mb-1">
                <Switch
                  {...field}
                  checked={field.value || parameter.schema.default || false}
                  onChange={(checked) => field.onChange(checked)}
                  label={
                    parameter.name +
                    (parameter.schema.title
                      ? ` - ${parameter.schema.title}`
                      : "")
                  }
                />
                {error && (
                  <p className="text-xs text-error mt-1">{error.message}</p>
                )}
              </div>
            )}
          />
        </div>
      );

    if (parameter.schema.type === "array") {
      const fieldName = parameter.name;
      const fieldDescription = parameter.schema.title;

      if (parameter.schema.items?.type) {
        return (
          <div key={index} className="w-full">
            <div className="mb-1">
              {!parameter.schema.suggest_endpoint ? (
                <Controller
                  name={name}
                  control={control}
                  defaultValue={[]}
                  rules={{
                    required: parameter.required
                      ? "Ce champ est requis"
                      : false,
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={(value || []).map((v: string) => ({
                          label: v,
                          value: v,
                        }))}
                        onChange={(newValue) => {
                          const values = (newValue as any[]).map((item) =>
                            typeof item === "string"
                              ? item
                              : item.label || item.value,
                          );
                          onChange(values);
                        }}
                        label={
                          fieldName +
                          (fieldDescription ? ` - ${fieldDescription}` : "")
                        }
                        placeholder="Type and press Enter..."
                        error={error?.message}
                      />
                    </>
                  )}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div className="w-full">
      <div className="space-y-3">
        {/* Endpoint required parameters — always visible */}
        {endpointRequired.length > 0 && (
          <div className="space-y-3">
            {endpointRequired.map((parameter, index) =>
              displayInput(parameter, index),
            )}
          </div>
        )}

        {/* Endpoint optional parameters — collapsible */}
        {endpointOptional.length > 0 && (
          <CollapsibleSection
            label={t("optional_parameters")}
            open={showEndpointOptional}
            onToggle={toggleEndpointOptional}
          >
            {endpointOptional.map((parameter, index) =>
              displayInput(parameter, index),
            )}
          </CollapsibleSection>
        )}

        {/* Gateway parameters — collapsible */}
        {gatewayParams.length > 0 && (
          <CollapsibleSection
            label={t("gateway_parameters")}
            open={showGateway}
            onToggle={toggleGateway}
          >
            {gatewayParams.map((parameter, index) =>
              displayInput(parameter, index),
            )}
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

export default TrackerForm;
